import styled from '@emotion/styled';

export const Page = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background-color: ${({ theme }): string => theme.color.grey100};
`;

export const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
`;

export const Logo = styled.img`
    max-width: 200px;
`;

export const ProductTitle = styled.h2`
    margin: 0;
    font-family: ${({ theme }): string => theme.font.family};
    font-size: ${({ theme }): string => theme.font.size.normal};
    color: ${({ theme }): string => theme.color.black200};
    letter-spacing: 5px;
    text-transform: uppercase;
`;

export const Card = styled.form`
    padding: 32px;
    box-shadow: 0px 0px 30px 10px rgba(0, 0, 0, 0.1);
    border-radius: ${({ theme }) => theme.radius.main};
    background-color: ${({ theme }): string => theme.color.white100};
    width: 100%;
    max-width: 480px;
`;

export const Title = styled.h1`
    margin: 0 0 8px 0;
    font-size: ${({ theme }): string => theme.font.size.large};
    font-family: ${({ theme }): string => theme.font.family};
    font-weight: ${({ theme }): string => theme.font.weight.medium.toString()};
    text-align: center;
`;

export const Subtitle = styled.p`
    margin: 0 0 24px 0;
    font-size: ${({ theme }): string => theme.font.size.normal};
    color: ${({ theme }): string => theme.color.grey500};
    text-align: center;
`;

export const QrCodeWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: 16px 0 24px 0;

    img {
        width: 200px;
        height: 200px;
    }
`;

export const CopySecretButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: fit-content;
    margin: 0 auto 20px;
    padding: 8px 14px;
    background: ${({ theme }): string => theme.color.white100};
    border: 1px solid ${({ theme }): string => theme.color.grey300};
    border-radius: ${({ theme }) => theme.radius.main};
    color: ${({ theme }): string => theme.color.grey500};
    cursor: pointer;
    font-family: ${({ theme }): string => theme.font.family};
    font-size: ${({ theme }): string => theme.font.size.normal};
    font-weight: ${({ theme }): number => theme.font.weight.medium};
    transition: border-color 120ms ease, color 120ms ease, background-color 120ms ease;

    &:hover,
    &:focus-visible {
        border-color: ${({ theme }): string => theme.color.grey400};
        color: ${({ theme }): string => theme.color.black};
        outline: none;
    }
`;

export const MethodChooserList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 16px 0 8px 0;
`;

export const MethodCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 16px;
    background: ${({ theme }): string => theme.color.white100};
    border: 1.5px solid ${({ theme }): string => theme.color.grey300};
    border-radius: ${({ theme }) => theme.radius.main};
    cursor: pointer;
    font-family: ${({ theme }): string => theme.font.family};
    text-align: left;
    transition: border-color 120ms ease, box-shadow 120ms ease;

    strong {
        font-size: ${({ theme }): string => theme.font.size.normal};
        font-weight: ${({ theme }): number => theme.font.weight.medium};
        color: ${({ theme }): string => theme.color.black};
    }

    span {
        font-size: ${({ theme }): string => theme.font.size.small};
        color: ${({ theme }): string => theme.color.grey500};
    }

    &:hover,
    &:focus {
        border-color: ${({ theme }): string => theme.color.primary};
        outline: none;
        box-shadow: 0 0 0 4px rgba(249, 109, 109, 0.15);
    }
`;

export const CodeInputWrapper = styled.div`
    margin-bottom: 16px;
`;

export const OtpBox = styled.input`
    width: 56px;
    height: 56px;
    text-align: center;
    font-size: ${({ theme }): string => theme.font.size.huge};
    font-family: ${({ theme }): string => theme.font.family};
    font-weight: ${({ theme }): string => theme.font.weight.medium.toString()};
    color: ${({ theme }): string => theme.color.black};
    background-color: ${({ theme }): string => theme.color.white100};
    border: 1.5px solid ${({ theme }): string => theme.color.grey400};
    border-radius: ${({ theme }) => theme.radius.main};
    outline: none;
    transition: border-color 120ms ease, box-shadow 120ms ease;

    &:focus {
        border-color: ${({ theme }): string => theme.color.primary};
        box-shadow: 0 0 0 4px rgba(249, 109, 109, 0.25);
    }

    &:disabled {
        background-color: ${({ theme }): string => theme.color.grey100};
        opacity: ${({ theme }): number => theme.opacity.disabled};
    }
`;

export const Actions = styled.div`
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const SecondaryLink = styled.button`
    background: none;
    border: none;
    color: ${({ theme }): string => theme.color.grey500};
    cursor: pointer;
    font-family: ${({ theme }): string => theme.font.family};
    font-size: ${({ theme }): string => theme.font.size.normal};
    font-weight: ${({ theme }): number => theme.font.weight.medium};
    padding: 8px 0;
    text-decoration: none;
    transition: color 120ms ease;

    &:hover,
    &:focus-visible {
        color: ${({ theme }): string => theme.color.primary};
        outline: none;
    }
`;

export const BackupCodesList = styled.ul`
    margin: 16px 0;
    padding: 16px;
    background-color: ${({ theme }): string => theme.color.grey100};
    border-radius: ${({ theme }): string => theme.radius.main};
    font-family: monospace;
    list-style: none;
    column-count: 2;
`;

export const BackupCodeItem = styled.li`
    padding: 4px 0;
    font-size: ${({ theme }): string => theme.font.size.normal};
`;

export const Tabs = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    border-bottom: 1px solid ${({ theme }): string => theme.color.grey300};
`;

export const TabButton = styled.button<{ active: boolean; disabled?: boolean }>`
    flex: 1;
    background: none;
    border: none;
    border-bottom: 2px solid ${({ theme, active }): string => (active ? theme.color.primary : 'transparent')};
    color: ${({ theme, active, disabled }): string => {
        if (disabled) return theme.color.grey400;
        return active ? theme.color.primary : theme.color.grey500;
    }};
    cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
    font-family: ${({ theme }): string => theme.font.family};
    font-size: ${({ theme }): string => theme.font.size.normal};
    font-weight: ${({ theme }): number => theme.font.weight.medium};
    padding: 12px 8px;
    margin-bottom: -1px;
    transition: color 120ms ease, border-color 120ms ease;
`;
